/**
 * Raw darkstore DTOs from gateway gRPC serialization (camelCase and snake_case tolerant).
 */

export interface RemoteDeliveryZoneDto {
  id?: string;
  storeId?: string;
  store_id?: string;
  name?: string;
  is_active?: boolean;
  isActive?: boolean;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  polygonGeojson?: string;
  polygon_geojson?: string;
}

export interface RemoteStoreDto {
  id?: string;
  storeCode?: string;
  store_code?: string;
  name?: string;
  displayName?: string;
  slug?: string;
  addressLine1?: string;
  address_line1?: string;
  addressLine2?: string | null;
  address_line2?: string | null;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  managerId?: string | null;
  manager_id?: string | null;
  managerName?: string | null;
  manager_name?: string | null;
  operatingHours?: string | unknown;
  operating_hours?: string | unknown;
  maxDeliveryKm?: number | string;
  max_delivery_km?: number | string;
  avgPackingMin?: number | string;
  avg_packing_min?: number | string;
  latitude?: number | string;
  longitude?: number | string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  deliveryZones?: RemoteDeliveryZoneDto[];
  delivery_zones?: RemoteDeliveryZoneDto[];
}

export interface RemoteStoresListBody extends Record<string, unknown> {
  items?: RemoteStoreDto[];
  total?: number;
  page?: number;
  limit?: number;
  pageCount?: number;
}

function pickNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * Parses unwrapped list body shape from `{ items, total, page, limit }` or array.
 */
export function parseStoresPagedFromUnwrappedData(raw: unknown): {
  items: RemoteStoreDto[];
  total?: number;
  page?: number;
  limit?: number;
  pageCount?: number;
} {
  if (Array.isArray(raw)) {
    return {
      items: raw as RemoteStoreDto[],
      total: raw.length,
      page: 1,
      limit: raw.length,
      pageCount: 1,
    };
  }

  if (!raw || typeof raw !== 'object') {
    return { items: [] };
  }

  const o = raw as Record<string, unknown>;
  const itemsRaw = o.items ?? o.data ?? o.stores;
  const items = Array.isArray(itemsRaw) ? (itemsRaw as RemoteStoreDto[]) : [];

  const total =
    pickNumber(o.total) ??
    pickNumber(o.count) ??
    pickNumber(o.totalItems) ??
    pickNumber(o.totalCount);

  const page = pickNumber(o.page);
  const limit = pickNumber(o.limit);
  const pageCount = pickNumber(o.pageCount) ?? pickNumber(o.totalPages);

  return {
    items,
    total,
    page,
    limit,
    pageCount,
  };
}
